import React, { useState } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import RadioGroup from 'Components/DesignSystem/RadioGroup/RadioGroup';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Textarea from 'Components/DesignSystem/Textarea/Textarea';
import Tag from 'Components/DesignSystem/Tag/Tag';
import { RadioGroupOption } from 'types/DesignSystem/RadioGroup';
import { TISRProject } from './DefineModel/model';

type BulkApplyCSSModalProps = {
    open: boolean;
    selectedModels: TISRProject[];
    onClose: () => void;
    onConfirm: (css: string, mode: 'overwrite' | 'append') => Promise<void>;
    loading?: boolean;
};

const MODE_OPTIONS: RadioGroupOption[] = [
    {
        value: 'overwrite',
        label: (
            <div>
                <p className="tw-text-sm tw-font-medium tw-text-ink">Overwrite Existing CSS</p>
                <p className="tw-text-xs tw-text-ink-secondary">
                    Your CSS will replace the existing CSS on every UI Model. This cannot be undone.
                </p>
            </div>
        ),
        'data-testid': 'bulk-css-mode-overwrite',
    },
    {
        value: 'append',
        label: (
            <div>
                <p className="tw-text-sm tw-font-medium tw-text-ink">Append to Existing CSS</p>
                <p className="tw-text-xs tw-text-ink-secondary">
                    Your CSS will be added to the end of the existing CSS on every UI Model. Existing styles are preserved.
                </p>
            </div>
        ),
        'data-testid': 'bulk-css-mode-append',
    },
];

const BulkApplyCSSModal = ({ open, selectedModels, onClose, onConfirm, loading = false }: BulkApplyCSSModalProps) => {
    const [mode, setMode] = useState<'overwrite' | 'append'>('overwrite');
    const [css, setCss] = useState('');
    const [acknowledged, setAcknowledged] = useState(false);

    const guardedClose = () => { if (!loading) onClose(); };

    const isConfirmEnabled = mode === 'overwrite'
        ? css.trim().length > 0 && acknowledged
        : css.trim().length > 0;

    const handleConfirm = async () => {
        if (!isConfirmEnabled) return;
        await onConfirm(css, mode);
    };

    const handleModeChange = (value: string | number) => {
        const next = value === 'append' ? 'append' : 'overwrite';
        setMode(next);
        if (next === 'append') setAcknowledged(false);
    };

    return (
        <Dialog
            open={open}
            onClose={guardedClose}
            title="Apply CSS"
            size="md"
            data-testid="bulk-apply-css-modal"
            footer={
                <div className="tw-flex tw-w-full tw-gap-2 tw-justify-end">
                    <Button
                        variant="secondary"
                        disabled={loading}
                        onClick={guardedClose}
                        data-testid="bulk-apply-css-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        loading={loading}
                        disabled={!isConfirmEnabled}
                        onClick={handleConfirm}
                        data-testid="bulk-apply-css-confirm-btn"
                    >
                        {mode === 'overwrite' ? 'Overwrite CSS' : 'Append CSS'}
                    </Button>
                </div>
            }
        >
            <div className="tw-flex tw-flex-col tw-gap-4">
                <p className="tw-text-sm tw-text-ink-secondary" data-testid="bulk-apply-css-subtitle">
                    CSS will be written to every UI Model under each selected Guide Model.
                </p>
                <div>
                    <p className="tw-text-sm tw-font-medium tw-text-ink tw-mb-2">
                        Selected Model(s) ({selectedModels.length})
                    </p>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-max-h-28 tw-overflow-y-auto" data-testid="bulk-apply-css-chips">
                        {selectedModels.map((m) => (
                            <Tag
                                key={m.id}
                                label={m.ISRName}
                                variant="meta"
                                title={m.ISRName}
                                data-testid={`bulk-apply-css-chip-${m.id}`}
                            />
                        ))}
                    </div>
                </div>
                <RadioGroup
                    label="Select Mode"
                    options={MODE_OPTIONS}
                    value={mode}
                    onChange={handleModeChange}
                    name="bulk-css-mode"
                    data-testid="bulk-css-mode-group"
                />
                <Textarea
                    label="Add custom CSS"
                    value={css}
                    onChange={(v) => setCss(v)}
                    placeholder="Enter your CSS here…"
                    rows={5}
                    data-testid="bulk-css-textarea"
                />
                {mode === 'overwrite' && (
                    <Checkbox
                        checked={acknowledged}
                        onChange={(next) => setAcknowledged(next)}
                        label={
                            <span className="tw-text-xs tw-text-ink-secondary">
                                I understand this will overwrite the CSS on every UI Model under the selected Guide Models, and I have backed up the existing CSS.
                            </span>
                        }
                        labelClamp={false}
                        data-testid="bulk-css-ack-checkbox"
                    />
                )}
            </div>
        </Dialog>
    );
};

export default BulkApplyCSSModal;
